import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const linkVariants = cva(
  'inline-flex cursor-pointer items-center rounded-sm underline-offset-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25',
  {
    variants: {
      variant: {
        default: 'text-primary hover:underline',
        secondary: 'text-muted-foreground hover:text-foreground hover:underline',
        destructive: 'text-destructive hover:underline',
        external: 'text-primary gap-1 hover:underline',
      },
      size: {
        sm: 'text-xs font-medium',
        md: 'text-sm font-medium',
        lg: 'text-base font-semibold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

const externalIconSize: Record<string, string> = {
  sm: '10',
  md: '12',
  lg: '14',
};

function ExternalLinkSvg({ size }: { size: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export interface TextLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof linkVariants> {
  asChild?: boolean;
}

const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    const isExternal = variant === 'external';
    return (
      <Comp
        ref={ref}
        className={cn(linkVariants({ variant, size, className }))}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        <Slottable>{children}</Slottable>
        {isExternal && <ExternalLinkSvg size={externalIconSize[size ?? 'md']} />}
      </Comp>
    );
  },
);
TextLink.displayName = 'TextLink';

export { TextLink, linkVariants };
